/**
 * Centralized catalog of dynamic variables available in the template editor.
 *
 * Used by the RichTextEditor "Champs dynamiques" button to insert placeholders
 * at the cursor position. Keep this list in sync with the substitution layer
 * in `templateVariables.ts` and the runtime values populated by `ContractForm`.
 */

export interface AvailableVariable {
  /** Token inserted into the document (e.g. `{first_name}`). */
  token: string;
  /** Human-readable label shown in the menu. */
  label: string;
  /** Short description of what the variable resolves to at render time. */
  description?: string;
}

export interface VariableGroup {
  /** Group heading shown in the menu. */
  category: string;
  variables: AvailableVariable[];
}

export const AVAILABLE_VARIABLES: VariableGroup[] = [
  {
    category: 'Client',
    variables: [
      { token: '{first_name}', label: 'Prénom', description: 'Prénom du client' },
      { token: '{last_name}', label: 'Nom', description: 'Nom de famille du client' },
      { token: '{client.full_name}', label: 'Nom complet', description: 'Prénom + Nom' },
    ],
  },
  {
    category: 'Système',
    variables: [
      { token: '{date_today}', label: 'Date du jour', description: 'Date de signature' },
      { token: '{practitioner_name}', label: 'Praticien', description: 'Nom du praticien' },
    ],
  },
];

/**
 * Insert `insert` into `text` at `cursorPosition`, padding spaces so we never
 * collapse adjacent words (e.g. `Nom{first_name}` → `Nom {first_name}`).
 */
export function insertAtCursor(text: string, insert: string, cursorPosition: number): string {
  const safePos = Math.max(0, Math.min(cursorPosition, text.length));
  const before = text.slice(0, safePos);
  const after = text.slice(safePos);

  const needsLeadingSpace = before.length > 0 && !/\s$/.test(before) && !/^\s/.test(insert);
  const needsTrailingSpace = after.length > 0 && !/^\s/.test(after) && !/\s$/.test(insert);

  return (
    before +
    (needsLeadingSpace ? ' ' : '') +
    insert +
    (needsTrailingSpace ? ' ' : '') +
    after
  );
}
